/**
 * PurchaseToggle - Simplified Custom Element for product tab switching
 * Uses buttons with data attributes instead of complex input-based logic
 */
class PurchaseToggle extends HTMLElement {
  constructor() {
    super();
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleDropdownTabClick = this.handleDropdownTabClick.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDropdownKeydown = this.handleDropdownKeydown.bind(this);
    this.abortController = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  /**
   * Initialize the component
   */
  init() {
    console.log('Initializing simplified PurchaseToggle:', this.id);

    // Cache elements
    this.tabButtons = this.querySelectorAll('.tab-button');
    this.isCombinedListing = this.dataset.combinedListing === 'true';
    this.isDropdown = this.dataset.displayStyle === 'dropdown' || this.classList.contains('is--dropdown');
    
    // Check if we're inside a dialog/modal
    this.isInsideDialog = this.checkIfInsideDialog();

    // Set up event listeners
    this.attachEventListeners();

    if (this.isDropdown) {
      this.setupDropdown();
    }

    // Initialize active state
    this.initializeActiveState();
  }

  /**
   * Set up dropdown-specific behavior
   */
  setupDropdown() {
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDropdownKeydown);
    this.updateDropdownAria();
  }

  handleDropdownKeydown(event) {
    if (!this.isDropdown || !this.classList.contains('is--open')) return;
    if (event.key === 'Escape') {
      this.closeDropdown();
    }
  }

  handleDocumentClick(event) {
    if (!this.isDropdown || !this.classList.contains('is--open')) return;
    if (!this.contains(event.target)) {
      this.closeDropdown();
    }
  }

  openDropdown() {
    this.classList.add('is--open');
    this.updateDropdownAria();
  }

  closeDropdown() {
    this.classList.remove('is--open');
    this.updateDropdownAria();
  }

  toggleDropdown() {
    if (this.classList.contains('is--open')) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  updateDropdownAria() {
    if (!this.isDropdown) return;

    const isOpen = this.classList.contains('is--open');
    this.tabButtons.forEach((btn) => {
      if (btn.classList.contains('active')) {
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      } else {
        btn.removeAttribute('aria-expanded');
      }
    });
  }

  /**
   * Keep the active tab as the first item in dropdown mode
   */
  moveActiveTabToTop() {
    if (!this.isDropdown) return;

    const tabsContainer = this.querySelector('.purchase-options-toggle__tabs');
    const activeTab = this.querySelector('.tab-button.active');

    if (tabsContainer && activeTab && tabsContainer.firstElementChild !== activeTab) {
      tabsContainer.prepend(activeTab);
    }

    this.tabButtons = this.querySelectorAll('.tab-button');
  }

  /**
   * Handle tab clicks in dropdown mode
   */
  handleDropdownTabClick(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const isActive = button.classList.contains('active');

    if (isActive) {
      this.toggleDropdown();
      return;
    }

    this.closeDropdown();
    this.handleTabClick(event);
  }

  /**
   * Check if the purchase toggle is inside a dialog/modal
   */
  checkIfInsideDialog() {
    // Check for common dialog/modal selectors
    const dialog = this.closest('dialog');
    const quickAddModal = this.closest('[data-quick-add-modal]');
    const productQuickAdd = this.closest('.product-quick-add');
    const modal = this.closest('[role="dialog"]');
    
    return !!(dialog || quickAddModal || productQuickAdd || modal);
  }

  /**
   * Attach event listeners to tab buttons
   */
  attachEventListeners() {
    const clickHandler = this.isDropdown ? this.handleDropdownTabClick : this.handleTabClick;

    this.tabButtons.forEach(button => {
      button.addEventListener('click', clickHandler);
    });
  }

  /**
   * Initialize which tab should be active on load
   */
  initializeActiveState() {
    // Remove all active states first
    this.clearActiveStates();

    // Find the tab that matches the current product
    let activeTab = this.findMatchingTabForCurrentProduct();

    // Fall back to default selected or first tab if no match found
    if (!activeTab) {
      console.log('No product match found, using fallback logic');
      activeTab = this.querySelector('.tab-button.active') ||  // Uses block.settings.is_default_selected 
        this.tabButtons[0];
      console.log('Fallback tab selected:', activeTab?.dataset?.productId);
    }

    if (activeTab) {
      this.setActiveTab(activeTab, false); // Don't trigger content load on init

      // Activate purchase options after initialization
      this.activatePurchaseOptions(activeTab);
    }
  }

  normalizeToCurrentOrigin(productUrl) {
    if (!productUrl) return '';

    try {
      const parsedUrl = new URL(productUrl, window.location.origin);
      return `${window.location.origin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch (error) {
      console.warn('Failed to normalize URL:', productUrl, error);
      return productUrl;
    }
  }

  /**
   * Find the tab that matches the current product page
   */
  findMatchingTabForCurrentProduct() {
    const currentLocation = new URL(window.location.href);
    const currentPath = currentLocation.pathname;
    const currentView = currentLocation.searchParams.get('view') || '';
    const currentProductHandle = this.extractProductHandleFromUrl(currentPath);

    console.log('Finding matching tab for current product:', currentProductHandle, currentPath, currentView);

    // Priority match: same path and same `view` param.
    // This handles templates like /products/inositol vs /products/inositol?view=inositol_pcos.
    for (const button of this.tabButtons) {
      const buttonProductUrl = button.dataset.productUrl;
      if (!buttonProductUrl) continue;

      let buttonUrl;
      try {
        buttonUrl = new URL(buttonProductUrl, window.location.origin);
      } catch (error) {
        console.warn('Invalid product URL on tab button:', buttonProductUrl, error);
        continue;
      }

      const buttonView = buttonUrl.searchParams.get('view') || '';

      if (buttonUrl.pathname === currentPath && buttonView === currentView) {
        console.log('Found matching tab by path + view:', buttonUrl.pathname, buttonView);
        return button;
      }
    }

    // Primary matching: exact handle comparison
    for (const button of this.tabButtons) {
      const buttonProductUrl = button.dataset.productUrl;

      if (buttonProductUrl) {
        const buttonProductHandle = this.extractProductHandleFromUrl(buttonProductUrl);

        console.log('Comparing:', currentProductHandle, 'vs', buttonProductHandle);

        if (currentProductHandle && buttonProductHandle &&
          currentProductHandle === buttonProductHandle) {
          console.log('Found matching tab by handle:', buttonProductHandle);
          return button;
        }
      }
    }

    // Legacy fallback: simple URL matching for when complex logic fails
    console.log('Primary matching failed, trying legacy URL matching...');
    for (const button of this.tabButtons) {
      const buttonProductUrl = button.dataset.productUrl;

      if (buttonProductUrl) {
        let parsedButtonUrl;
        try {
          parsedButtonUrl = new URL(buttonProductUrl, window.location.origin);
        } catch (error) {
          console.warn('Invalid product URL on tab button (legacy matching):', buttonProductUrl, error);
          continue;
        }

        const buttonPath = parsedButtonUrl.pathname;

        // Simple check: if current path is exactly the button's product URL
        if (currentPath === buttonPath) {
          console.log('Found matching tab by exact URL match:', buttonProductUrl);
          return button;
        }

        // Check if current URL contains the button's product URL (for variants, etc.)
        if (currentPath.startsWith(buttonPath)) {
          console.log('Found matching tab by URL starts with:', buttonProductUrl);
          return button;
        }

        // Check if button's URL is contained in current URL (reverse check)
        if (buttonPath.startsWith(currentPath)) {
          console.log('Found matching tab by button URL starts with current:', buttonProductUrl);
          return button;
        }
      }
    }

    console.log('No matching tab found for current product');
    return null;
  }

  /**
   * Extract product handle from URL path
   */
  extractProductHandleFromUrl(url) {
    if (!url) return null;

    // Handle both full URLs and paths
    const urlPath = url.includes('://') ? new URL(url).pathname : url;

    // Extract product handle from /products/handle-name pattern
    // This handles URLs like /products/constipation-test?variant=123 or /products/constipation-test
    // this also should handle urls like /pages/lean-define-duo
    const match = urlPath.match(/\/products\/([^\/\?]+)/);
    if (!match) {
      const match = urlPath.match(/\/pages\/([^\/\?]+)/);
      return match ? match[1] : null;
    }
    return match ? match[1] : null;
  }

  /**
   * Handle tab button clicks
   */
  handleTabClick(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const previousActiveButton = this.querySelector('.tab-button.active');
    console.log('Tab clicked:', button.dataset.productId);

    // Re-check if we're inside a dialog (in case element was moved)
    this.isInsideDialog = this.checkIfInsideDialog();

    this.trackFlavorTabChange(previousActiveButton, button);
    this.setActiveTab(button, true);
  }

  getFlavorLabelFromTab(button) {
    if (!button) return '';

    const explicitFlavor = button.dataset.flavor || button.dataset.tabTitle || '';
    if (explicitFlavor) return explicitFlavor.trim();

    const titleNode = button.querySelector('.tab-title');
    return titleNode?.textContent?.trim() || '';
  }

  getProductContextForAnalytics() {
    let productId = '';
    let productTitle = '';

    try {
      const productJsonEl = document.querySelector('[data-product-json]');
      if (productJsonEl?.textContent) {
        const productData = JSON.parse(productJsonEl.textContent);
        productId = productData?.id != null ? String(productData.id) : '';
        productTitle = typeof productData?.title === 'string' ? productData.title : '';
      }
    } catch (error) {
      console.warn('Failed to parse product JSON for flavor analytics', error);
    }

    return { productId, productTitle };
  }

  trackFlavorTabChange(previousButton, nextButton) {
    if (!nextButton) return;

    const fromFlavor = this.getFlavorLabelFromTab(previousButton);
    const toFlavor = this.getFlavorLabelFromTab(nextButton);
    const fromProductId = previousButton?.dataset?.productId ? String(previousButton.dataset.productId) : '';
    const toProductId = nextButton?.dataset?.productId ? String(nextButton.dataset.productId) : '';

    // Ignore no-op clicks on already active flavor tab
    if (!toProductId || toProductId === fromProductId) return;

    window.__pdpFlavorAnalyticsState = window.__pdpFlavorAnalyticsState || { clickCount: 0 };
    const globalState = window.__pdpFlavorAnalyticsState;
    globalState.clickCount += 1;
    globalState.lastFlavor = toFlavor;
    globalState.lastProductId = toProductId;

    const { productId, productTitle } = this.getProductContextForAnalytics();

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'flavor_change',
      event_category: 'PDP',
      event_label: `${fromFlavor || 'unknown'} -> ${toFlavor || 'unknown'}`,
      click_count: globalState.clickCount,
      from_flavor: fromFlavor,
      to_flavor: toFlavor,
      from_product_id: fromProductId,
      to_product_id: toProductId,
      product_id: productId || toProductId,
      product_title: productTitle,
      flavor_change_source: 'purchase_toggle_tab'
    });
  }

  /**
   * Set a tab as active
   */
  setActiveTab(button, shouldLoadContent = true) {
    // Clear all active states
    this.clearActiveStates();

    // Set active tab
    button.classList.add('active');
    button.setAttribute('aria-selected', 'true');

    if (this.isDropdown) {
      this.moveActiveTabToTop();
      this.updateDropdownAria();
    }

    const contentId = button.dataset.contentId;

    if (shouldLoadContent) {
      // If inside a dialog, always fetch and replace dialog content
      if (this.isInsideDialog) {
        // Check if we have a product URL to fetch
        const productUrl = button.dataset.productUrl;
        if (productUrl) {
          this.loadProductInDialog(button);
        } else {
          // Fallback to normal content switching if no URL
          this.switchContent(contentId);
          this.activatePurchaseOptions(button);
        }
      } else if (this.isCombinedListing) {
        // Main page with combined listing - update main page
        this.loadProductAsync(button);
      } else {
        // Main page without combined listing - just switch content visibility
        this.switchContent(contentId);
        this.activatePurchaseOptions(button);
      }
    }
  }

  /**
   * Clear all active states
   */
  clearActiveStates() {
    this.tabButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });
  }

  /**
   * Switch content visibility (non-combined listing mode)
   */
  switchContent(targetContentId) {
    // Hide all purchase option content
    const allContent = document.querySelectorAll('[id^="purchase-option-"]');
    allContent.forEach(content => {
      content.classList.remove('active');
      content.style.display = 'none';
    });

    // Show target content
    const targetContent = document.getElementById(targetContentId);
    if (targetContent) {
      targetContent.classList.add('active');
      targetContent.style.display = 'flex';
    }
  }

  /**
   * Activate purchase options for the selected tab
   */
  activatePurchaseOptions(button) {
    const contentId = button.dataset.contentId;
    const defaultToSubscription = button.dataset.defaultToSubscription === 'true';

    const purchaseOptions = document.getElementById(contentId);
    if (!purchaseOptions) {
      console.error('Target content not found:', contentId);
      return;
    }

    // Deactivate all other purchase options
    document.querySelectorAll('purchase-options').forEach(el => {
      el.classList.remove('active');
    });

    // Activate this purchase options element
    purchaseOptions.classList.add('active');

    // Set purchase type based on settings
    this.setPurchaseType(purchaseOptions, defaultToSubscription);
  }

  /**
   * Set the purchase type (subscription or one-time) using buttons instead of inputs
   */
  setPurchaseType(purchaseOptions, defaultToSubscription) {
    // Find subscription and one-time buttons/tabs
    const subscriptionButton = purchaseOptions.querySelector('.purchase-options__tab-nav--subscription');
    const onetimeButton = purchaseOptions.querySelector('.purchase-options__tab-nav--onetime');

    if (!subscriptionButton && !onetimeButton) {
      console.log('No purchase type buttons found');
      return;
    }

    // Decide which type to activate
    const shouldActivateSubscription = defaultToSubscription && subscriptionButton;
    const targetButton = shouldActivateSubscription ? subscriptionButton : onetimeButton;

    if (targetButton) {
      // Set up purchase options event listener if not already done
      this.setupPurchaseOptionsListeners(purchaseOptions);

      // Activate the target button
      this.activatePurchaseTypeButton(targetButton, purchaseOptions);

      // Dispatch custom event for other components
      this.dispatchPurchaseTypeEvent(shouldActivateSubscription ? 'subscription' : 'onetime', purchaseOptions);
    }
  }

  /**
   * Set up event listeners for purchase options buttons
   */
  setupPurchaseOptionsListeners(purchaseOptions) {
    if (purchaseOptions.dataset.listenersSetup === 'true') {
      return; // Already set up
    }

    const tabButtons = purchaseOptions.querySelectorAll('.purchase-options__tab-nav');
    tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.activatePurchaseTypeButton(button, purchaseOptions);

        // Trigger the purchase options element to update
        if (typeof purchaseOptions.handleTabSwitch === 'function') {
          purchaseOptions.handleTabSwitch();
        }

        // Trigger purchase type update
        if (typeof purchaseOptions.updatePurchaseType === 'function') {
          purchaseOptions.updatePurchaseType();
        }
      });
    });

    purchaseOptions.dataset.listenersSetup = 'true';
  }

  /**
   * Activate a purchase type button
   */
  activatePurchaseTypeButton(targetButton, purchaseOptions) {
    const allButtons = purchaseOptions.querySelectorAll('.purchase-options__tab-nav');
    const allContent = purchaseOptions.querySelectorAll('.purchase-options__content');

    // Clear all active states
    allButtons.forEach(btn => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });

    allContent.forEach(content => {
      content.classList.remove('active');
    });

    // Activate target button
    targetButton.classList.add('active');
    targetButton.setAttribute('aria-selected', 'true');

    // Activate corresponding content
    const purchaseType = targetButton.dataset.purchaseType;
    const targetContent = purchaseOptions.querySelector(`.purchase-options__content--${purchaseType}`);

    if (targetContent) {
      targetContent.classList.add('active');

      // Auto-select highest number option
      if (purchaseType === 'subscription') {
        this.autoSelectHighestSubscriptionOption(targetContent);
      } else {
        this.autoSelectHighestOnetimeOption(targetContent);
      }
    }
  }

  /**
   * Auto-select the subscription option with the highest number (e.g., 6 bottles vs 3 bottles)
   */
  autoSelectHighestSubscriptionOption(subscriptionContent) {
    const allOptions = subscriptionContent.querySelectorAll('.subscription-option__input');
    if (allOptions.length === 0) return;

    // Check if any option is already selected
    if (subscriptionContent.querySelector('.subscription-option__input:checked')) {
      return;
    }

    const selectedOption = this.findHighestNumberOption(allOptions);
    if (selectedOption) {
      selectedOption.checked = true;
      selectedOption.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Auto-select the one-time option with the highest number
   */
  autoSelectHighestOnetimeOption(onetimeContent) {
    const allOptions = onetimeContent.querySelectorAll('.onetime-option__input, input[name*="quantity"]');
    if (allOptions.length === 0) return;

    // Check if any option is already selected
    if (onetimeContent.querySelector('input:checked')) {
      return;
    }

    const selectedOption = this.findHighestNumberOption(allOptions);
    if (selectedOption) {
      selectedOption.checked = true;
      selectedOption.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Find the option with the highest number in its value, text, or data attributes
   */
  findHighestNumberOption(options) {
    let highestOption = null;
    let highestNumber = -1;

    options.forEach(option => {
      const number = this.extractNumberFromOption(option);
      if (number > highestNumber) {
        highestNumber = number;
        highestOption = option;
      }
    });

    // If no numbers found, return the last option as fallback
    return highestOption || options[options.length - 1];
  }

  /**
   * Extract number from option's data-quantity, value, or label text
   */
  extractNumberFromOption(option) {
    // Prioritize data-quantity attribute (consistent with PurchaseOptionsElement)
    const dataQuantity = parseInt(option.dataset.quantity);
    if (!isNaN(dataQuantity) && dataQuantity > 0) {
      return dataQuantity;
    }

    // Fallback to other data attributes and sources
    const sources = [
      option.value,
      option.dataset.value,
      option.getAttribute('data-months'),
      option.getAttribute('data-bottles'),
      option.getAttribute('data-number')
    ];

    // Also check the option's label text
    const label = option.closest('.subscription-option, .onetime-option');
    if (label) {
      sources.push(label.textContent);
    }

    // Extract the highest number found
    let maxNumber = 0;
    sources.forEach(source => {
      if (source) {
        // Find all numbers in the string
        const numbers = source.toString().match(/\d+/g);
        if (numbers) {
          numbers.forEach(num => {
            const parsed = parseInt(num, 10);
            if (parsed > maxNumber) {
              maxNumber = parsed;
            }
          });
        }
      }
    });

    return maxNumber;
  }

  /**
   * Dispatch custom event when purchase type changes
   */
  dispatchPurchaseTypeEvent(type, purchaseOptions) {
    const event = new CustomEvent('purchase-toggle:activated', {
      detail: {
        type: type,
        element: purchaseOptions,
        contentId: purchaseOptions.id
      },
      bubbles: true
    });

    this.dispatchEvent(event);
  }

  /**
   * Load product asynchronously for combined listing (main page behavior)
   */
  async loadProductAsync(button) {
    const rawProductUrl = button.dataset.productUrl;
    const productUrl = this.normalizeToCurrentOrigin(rawProductUrl);

    if (!productUrl) {
      console.error('No product URL found for combined listing');
      return;
    }

    console.log('Loading product asynchronously:', productUrl);

    // Set loading state
    this.setLoadingState(true, button);

    try {
      // Abort any pending requests
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      const response = await fetch(productUrl, {
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      await this.updatePageContent(doc, productUrl, button);

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Error loading product:', error);
      }
    } finally {
      this.setLoadingState(false, button);
    }
  }

  /**
   * Load product inside dialog/modal (dialog-specific behavior)
   * Fetches the page and replaces dialog content instead of updating main page
   */
  async loadProductInDialog(button) {
    const rawProductUrl = button.dataset.productUrl;
    const productUrl = this.normalizeToCurrentOrigin(rawProductUrl);

    if (!productUrl) {
      console.error('No product URL found for dialog loading');
      return;
    }

    console.log('Loading product in dialog:', productUrl);

    // Set loading state
    this.setLoadingState(true, button);

    try {
      // Abort any pending requests
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      const response = await fetch(productUrl, {
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      await this.updateDialogContent(doc, productUrl, button);

    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
      } else {
        console.error('Error loading product in dialog:', error);
      }
    } finally {
      this.setLoadingState(false, button);
    }
  }

  /**
   * Update dialog content after async load (instead of main page)
   */
  async updateDialogContent(doc, productUrl, button) {
    // Find the dialog container
    const dialog = this.closest('dialog') || 
                   this.closest('[data-quick-add-modal]') ||
                   this.closest('.product-quick-add') ||
                   this.closest('[role="dialog"]');
    
    if (!dialog) {
      console.error('Dialog container not found');
      return;
    }

    // Find the content container (similar to quick add modal)
    const modalContent = dialog.querySelector('[data-product-upsell-ajax]') ||
                        dialog.querySelector('.product-quick-add__inner') ||
                        dialog.querySelector('[data-product-upsell-container]') ||
                        dialog.querySelector('.drawer__inner');

    if (!modalContent) {
      console.error('Dialog content container not found');
      return;
    }

    // Extract the product-main section from the fetched page (same as quick add)
    const productMainSection = doc.querySelector('.section-product-main') || 
                              doc.querySelector('[class*="product-main"]') ||
                              doc.querySelector('#MainContent .product-main') ||
                              doc.querySelector('main');

    if (productMainSection) {
      // Store the selected button info before replacing content
      const selectedProductId = button.dataset.productId;
      const selectedProductUrl = button.dataset.productUrl;
      
      // Replace dialog content
      modalContent.innerHTML = productMainSection.innerHTML;

      // Reinitialize components in the dialog
      this.reinitializeDialogComponents(dialog);

      // Find and activate the matching purchase toggle and tab after content update
      // Wait a bit for the DOM to settle and custom elements to initialize
      setTimeout(() => {
        const newPurchaseToggle = dialog.querySelector('purchase-toggle');
        if (newPurchaseToggle && newPurchaseToggle.setActiveTab) {
          // Find the button that matches the selected product
          const matchingButton = newPurchaseToggle.querySelector(
            `[data-product-id="${selectedProductId}"], [data-product-url="${selectedProductUrl}"]`
          );
          
          if (matchingButton) {
            // Set the active tab without triggering another load
            newPurchaseToggle.setActiveTab(matchingButton, false);
            if (newPurchaseToggle.activatePurchaseOptions) {
              newPurchaseToggle.activatePurchaseOptions(matchingButton);
            }
          } else {
            // Fallback: initialize normally if method exists
            if (typeof newPurchaseToggle.initializeActiveState === 'function') {
              newPurchaseToggle.initializeActiveState();
            }
          }
        } else if (newPurchaseToggle) {
          // If element exists but methods aren't ready, wait a bit more
          setTimeout(() => {
            if (newPurchaseToggle.setActiveTab) {
              const matchingButton = newPurchaseToggle.querySelector(
                `[data-product-id="${selectedProductId}"], [data-product-url="${selectedProductUrl}"]`
              );
              if (matchingButton) {
                newPurchaseToggle.setActiveTab(matchingButton, false);
                if (newPurchaseToggle.activatePurchaseOptions) {
                  newPurchaseToggle.activatePurchaseOptions(matchingButton);
                }
              }
            }
          }, 200);
        }
      }, 100);
    } else {
      console.error('Could not find product-main section in fetched page');
    }
  }

  /**
   * Reinitialize Shopify components inside dialog after content update
   */
  reinitializeDialogComponents(dialog) {
    // Reinitialize Shopify Payment Buttons
    if (window?.Shopify?.PaymentButton?.init) {
      window.Shopify.PaymentButton.init();
    }

    // Trigger custom events for other components
    dialog.dispatchEvent(new Event('product:loaded'));
    dialog.dispatchEvent(new Event('dom:updated'));
    
    // Also dispatch globally for any listeners
    window.dispatchEvent(new Event('product:loaded'));
    document.dispatchEvent(new Event('dom:updated'));
  }

  /**
   * Update page content after async load
   */
  async updatePageContent(doc, productUrl, button) {
    // Update URL
    window.history.replaceState({}, '', productUrl);

    // Update page title
    const newTitle = doc.querySelector('title');
    if (newTitle) {
      document.title = newTitle.textContent;
    }

    // Update main content
    const newMain = doc.querySelector('main');
    const currentMain = document.querySelector('main');

    if (newMain && currentMain) {
      currentMain.innerHTML = newMain.innerHTML;

      // Reinitialize components
      this.reinitializeComponents();

      // Set active tab after content update
      console.log('Setting active tab after content update:', button.dataset.productId);
      this.setActiveTab(button, false);
      this.activatePurchaseOptions(button);
    }
  }

  /**
   * Reinitialize Shopify components after content update
   */
  reinitializeComponents() {
    // Reinitialize Shopify Payment Buttons
    if (window?.Shopify?.PaymentButton?.init) {
      window.Shopify.PaymentButton.init();
    }

    // Trigger custom events for other components
    window.dispatchEvent(new Event('product:loaded'));
    document.dispatchEvent(new Event('dom:updated'));
  }

  /**
   * Set loading state
   */
  setLoadingState(loading, button) {
    if (loading) {
      button.style.opacity = '0.7';
      button.disabled = true;
      document.body.style.cursor = 'wait';
    } else {
      button.style.opacity = '1';
      button.disabled = false;
      document.body.style.cursor = 'default';
    }
  }

  /**
   * Cleanup when element is removed
   */
  cleanup() {
    if (this.abortController) {
      this.abortController.abort();
    }

    const clickHandler = this.isDropdown ? this.handleDropdownTabClick : this.handleTabClick;

    this.tabButtons.forEach(button => {
      button.removeEventListener('click', clickHandler);
    });

    if (this.isDropdown) {
      document.removeEventListener('click', this.handleDocumentClick);
      document.removeEventListener('keydown', this.handleDropdownKeydown);
    }
  }
}

// Register the custom element
if (!customElements.get('purchase-toggle')) {
  customElements.define('purchase-toggle', PurchaseToggle);
}